//---------------------------------------------------------
// Imports
//---------------------------------------------------------
import {styled} from '@mui/material/styles';
import {SHA256} from 'crypto-js';
import {Cookies} from 'react-cookie';
import {IValueSet} from 'types';

const tolerance = 1e-8;

//---------------------------------------------------------
// Functions
//---------------------------------------------------------
export function get_localtime(date: Date | string, disable_hour?: boolean): string {
	const date_obj = typeof date === 'string' ? new Date(date) : date;

	if (!disable_hour) {
		// time format is '2021-10-10, 14:55:33'
		const date_time_str = date_obj.toLocaleString('en-US', {hour12: false});
		return date_time_str;
	} else {
		const date_str = date_obj.toLocaleDateString('en-US');
		return date_str;
	}
}

export function extract_number_from_str(str: string): number | null {
	const stringWithoutCommas = str.replace(/,/g, '');
	const regex = /\d+(\.\d+)?/;
	const match = stringWithoutCommas.match(regex);

	return match ? parseFloat(match[0]) : null;
}

export function capitalizeFirstLetter(str: string): string {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatNumber(num: number) {
	const formatter = new Intl.NumberFormat('en-US', {notation: 'compact', compactDisplay: 'short'});
	return formatter.format(num);
}

const cookie = new Cookies();
export function save_cookie(name: string, value: string) {
	cookie.set(name, value, {path: '/', secure: process.env.REACT_APP_ENV === 'prod', sameSite: process.env.REACT_APP_ENV === 'prod' ? 'none' : 'lax'});
}

export function get_cookie(name: string): string {
	const value = cookie.get(name);
	return value ? value.toString() : '';
}

export function remove_cookie(name: string) {
	cookie.remove(name, {path: '/'});
}

export function save_local_storage(name: string, value: string) {
	localStorage.setItem(name, value);
}

export function get_local_storage(name: string): string | null {
	return localStorage.getItem(name);
}

export function remove_local_storage(name: string) {
	localStorage.removeItem(name);
}

export function make_time_str(diff: number) {
	const sec = Math.floor((diff / 1000) % 60);
	const min = Math.floor((diff / (1000 * 60)) % 60);
	const hour = Math.floor((diff / (1000 * 60 * 60)) % 24);
	const day = Math.floor(diff / (1000 * 60 * 60 * 24));

	return `${day} days and ${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
}

export function check_email_format(email: string): boolean {
	const email_reg_exp = /^[a-zA-Z0-9+-\\_.]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
	return email_reg_exp.test(email);
}

export function get_hashed_string(str: string): string {
	return SHA256(str).toString();
}

export function clean_number(num: number): number {
	const precision = 8;
	const factor = Math.pow(10, precision);
	const rounded = Math.round(num * factor) / factor;

	const nearestInteger = Math.round(rounded);
	if (Math.abs(rounded - nearestInteger) < tolerance) return nearestInteger;
	else return rounded;
}

export function is_almost_equal(a: number, b: number): boolean {
	return Math.abs(a - b) < tolerance;
}

export const Highlight = styled('span')((props: {color: string; endColor?: string}) => ({
	color: props.endColor ? 'transparent' : props.color || 'inherit',
	backgroundImage: props.endColor ? `linear-gradient(to bottom, ${props.color}, ${props.endColor})` : 'none',
	backgroundClip: props.endColor ? 'text' : 'none',
	WebkitBackgroundClip: props.endColor ? 'text' : 'none',
}));

export function is_mobile_device(): boolean {
	// for debug // if (process.env.REACT_APP_ENV === 'local') return false;
	const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
	return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
}

export function is_standalone() {
	return (window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches;
}

export function get_os(): 'ios' | 'android' | 'other' {
	const userAgent = window.navigator.userAgent.toLowerCase();

	if (/iphone|ipad|ipod|mac/.test(userAgent) && navigator.maxTouchPoints > 1) return 'ios';
	else if (/android/.test(userAgent)) return 'android';
	else return 'other';
}

export function is_support_pwa_a2hs(): boolean {
	const os = get_os();
	const ua = window.navigator.userAgent;

	switch (os) {
		case 'ios':
			const iOSVersion = parseFloat((ua.match(/OS (\d+)_(\d+)_?(\d+)?/) || [])[1]);
			return iOSVersion >= 13.0;

		case 'android':
			const chromeVersion = parseInt((ua.match(/Chrome\/(\d+)/) || [])[1]);
			return chromeVersion >= 72;

		default:
			return 'onbeforeinstallprompt' in window;
	}
}

export const prevent_scroll = {
	overflowX: 'hidden',
	overflowY: 'hidden',
	touchAction: 'none',
	WebkitOverflowScrolling: 'none',
	overscrollBehavior: 'none',
	scrollbarWidth: 'none',
	msOverflowStyle: 'none',
	'&::-webkit-scrollbar': {display: 'none'},
};

export const allow_scroll_y = {
	overflowX: 'hidden',
	overflowY: 'auto',
	touchAction: 'pan-y',
	WebkitOverflowScrolling: 'none',
	overscrollBehavior: 'contain',
	scrollbarWidth: 'none',
	msOverflowStyle: 'none',
	'&::-webkit-scrollbar': {display: 'none'},
};

export const allow_scroll_x = {
	overflowX: 'auto',
	overflowY: 'hidden',
	touchAction: 'pan-x',
	WebkitOverflowScrolling: 'touch',
	overscrollBehavior: 'contain',
	scrollbarWidth: 'none',
	msOverflowStyle: 'none',
	'&::-webkit-scrollbar': {display: 'none'},
};

export function get_filtered_number(value: number) {
	const base_value = Number(Math.abs(value) < 100 ? value.toFixed(2) : value.toFixed(0));
	return base_value;
}

export function get_price_str(value: number, hide_plus_sign?: boolean, fraction?: number): string {
	const base_value = value === undefined || Math.abs(value) <= tolerance ? 0 : value;
	const _fraction = fraction !== undefined ? fraction : Math.abs(base_value) < 100 ? 2 : 0;
	const _hide_sign = base_value === 0 ? true : hide_plus_sign;
	return base_value.toLocaleString('ko-KR', {signDisplay: _hide_sign ? undefined : 'always', minimumFractionDigits: _fraction, maximumFractionDigits: _fraction});
}

export function get_percent_str(value: number, hide_plus_sign?: boolean, fraction?: number): string {
	const base_value = value === undefined || Math.abs(value) <= tolerance ? 0 : value;
	const _fraction = fraction ?? 2;
	const _hide_sign = base_value === 0 ? true : hide_plus_sign;
	return base_value.toLocaleString('ko-KR', {signDisplay: _hide_sign ? undefined : 'always', minimumFractionDigits: _fraction, maximumFractionDigits: _fraction}) + '%';
}

export function get_value(value_set: IValueSet): number {
	return value_set.ref_value * value_set.modifier + (value_set.calibrator ?? 0);
}

export function get_refresh_time(): number {
	return 1000;
}

export function get_exchange_list(): string[] {
	return process.env.REACT_APP_EXCHANGE_LIST?.split(',') || ['mock'];
}

export function get_ticker(exchange: string, market: string): string {
	if (exchange === 'upbit' || exchange === 'mock') {
		// KRW-BTC
		return market.split('-')[1];
	} else if (exchange === 'binance') {
		// BNBUSDT
		const ticker = market.replace(/_[0-9]{6}$/, '');
		return ticker.replace('USDT', ''); // "BTCUSDT" -> "BTC"
	} else {
		return market.split('/')[1]; // "BTC/USD" -> "USD"
	}
}

export function forced_relocation_to_login() {
	if (!window.location.href.includes('/login')) {
		console.error('Unauthorized, redirecting to login page');
		window.location.href = '/login';
	}
}

export function get_inset_top() {
	return 'env(safe-area-inset-top)';
}

export function get_inset_bottom() {
	return 'env(safe-area-inset-bottom)';
}

export const text_shadow_css =
	'-0.5px -0.5px 0 rgba(255, 255, 255, 0.7),\
	0.5px -0.5px 0 rgba(255, 255, 255, 0.7),\
	-0.5px 0.5px 0 rgba(255, 255, 255, 0.7),\
	0.5px 0.5px 0 rgba(255, 255, 255, 0.7)';

export function get_currency_mark(exchange: string) {
	return exchange === 'binance' ? '$' : '₩';
}

export function get_currency(exchange: string) {
	return exchange === 'binance' ? 'USDT' : 'KRW';
}

export function isMobileDevice(): boolean {
	const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
	return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
}

export function isStandaloneApp(): boolean {
	return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
}

export const preventScroll = {
	userSelect: 'none',
	overflowX: 'hidden',
	overflowY: 'hidden',
	touchAction: 'none',
	WebkitOverflowScrolling: 'auto',
	overscrollBehavior: 'none',
	overscrollBehaviorY: 'none',
	overscrollBehaviorX: 'none',
	scrollbarWidth: 'none',
	msOverflowStyle: 'none',
	'&::-webkit-scrollbar': {display: 'none'},
	'&, & *': {
		overscrollBehavior: 'none',
		overscrollBehaviorY: 'none',
		overscrollBehaviorX: 'none',
	},
};

export const allowScrollY = {
	overflowX: 'hidden',
	overflowY: 'auto',
	touchAction: 'pan-y',
	WebkitOverflowScrolling: 'none',
	overscrollBehavior: 'contain',
	scrollbarWidth: 'none',
	msOverflowStyle: 'none',
};

export const allowScrollX = {
	overflowX: 'auto',
	overflowY: 'hidden',
	touchAction: 'pan-x',
	WebkitOverflowScrolling: 'touch',
	overscrollBehavior: 'contain',
	scrollbarWidth: 'none',
	msOverflowStyle: 'none',
	'&::-webkit-scrollbar': {display: 'none'},
};
